<template>
  <div
    class="limits-banner"
    :class="{ compact: compact, 'no-label': !shouldShowLabel }"
    data-testid="limits-banner"
  >
    <!-- Limit Text -->
    <div v-if="shouldShowLabel" class="limits-text">
      <template v-if="label">
        <span class="label">{{ label }}</span>
      </template>
      <template v-else>
        <span class="label">Limit:&nbsp;</span>
        <span class="percentage">{{ percentage }}% reached</span>
      </template>
    </div>

    <!-- Upgrade Button -->
    <button
      v-if="showUpgrade"
      class="upgrade-button"
      @click="handleUpgrade"
      data-testid="limits-banner-upgrade"
    >
      <span class="upgrade-text">Upgrade</span>
      <img :src="upgradeCrown" alt="" class="crown-icon" />
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import upgradeCrown from './upgrade-crown.svg';

interface Props {
  percentage: number;
  label?: string;
  alwaysShowLabel?: boolean;
  compact?: boolean;
  showUpgrade?: boolean;
  onUpgrade?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  percentage: 90,
  alwaysShowLabel: false,
  compact: false,
  showUpgrade: true,
});

const emit = defineEmits<{
  upgrade: [];
}>();

const shouldShowLabel = computed(() => {
  if (props.label) {
    return true;
  }
  if (props.alwaysShowLabel) {
    return true;
  }
  return props.percentage >= 50;
});

const handleUpgrade = () => {
  if (props.onUpgrade) {
    props.onUpgrade();
  }
  emit('upgrade');
};
</script>

<style scoped>
.limits-banner {
  @apply relative rounded-2xl bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 w-fit flex items-center py-1.5 pl-3.5 pr-1.5 gap-3 text-left text-xs transition-colors;
}

.limits-banner.compact {
  @apply py-1 pl-2.5 pr-1 gap-2;
}

.limits-banner.no-label {
  @apply pl-1.5 gap-0 border-transparent;
}

.limits-banner.compact.no-label {
  @apply pl-1 gap-0;
}

/* Limits text container */
.limits-text {
  @apply flex items-center font-bold tracking-tight leading-none text-orange-500 dark:text-orange-400;
}

/* Upgrade button */
.upgrade-button {
  @apply relative h-9 rounded-xl bg-orange-200 dark:bg-orange-800 flex items-center justify-center gap-1 px-3 py-2 transition-all active:scale-95 cursor-pointer;
}

/* Hover state overlay */
.upgrade-button::before {
  @apply absolute content-[''] inset-0 rounded-xl opacity-0 transition-opacity bg-black/10 dark:bg-white/10;
}

.upgrade-button:hover::before {
  @apply opacity-100;
}

.upgrade-button .upgrade-text {
  @apply relative text-xs leading-[18px] font-semibold bg-gradient-to-r from-[#E4983E] to-[#EB6C57] bg-clip-text text-transparent;
}

.crown-icon {
  @apply relative w-[18px] h-[18px] flex-shrink-0;
}

/* Compact mode adjustments */
.limits-banner.compact .upgrade-button {
  @apply h-7 px-2 py-1;
}

.limits-banner.compact .crown-icon {
  @apply w-[15px] h-[15px];
}
</style>
